import { useCallback } from "react";
import { useAtom } from "jotai";
import { AnimatePresence, motion } from "framer-motion";

import { SingleSelect } from "@/components/atoms/SingleSelect";
import { VerticalScrollList } from "@/components/molecules/VerticalScrollList";
import { IconButton } from "@/components/atoms/IconButton";
import { StreamingIcons } from "@/components/atoms/Icon";
import { useClassNames } from "@/utils/classNames";
import { videoPlayerAtom } from "@/constants/atoms";

import styles from "./SubtitleOverlay.module.scss";
import { SubtitleOverlayProps } from "./types";
import { SUBTITLE_OPTIONS, AUDIO_OPTIONS } from "./config";

/**
 * Subtitle Overlay molecule component
 * Displays a modal overlay with subtitle track selection
 *
 * @param props - The subtitle overlay properties
 * @returns the subtitle overlay component
 */
const SubtitleOverlay = ({ className }: SubtitleOverlayProps) => {
  const [videoPlayerState, setVideoPlayerState] = useAtom(videoPlayerAtom);
  const { isSubtitleOverlayOpen, selectedSubtitle, selectedAudio } =
    videoPlayerState;

  const handleClose = useCallback(() => {
    setVideoPlayerState((prev) => ({
      ...prev,
      isSubtitleOverlayOpen: false,
    }));
  }, [setVideoPlayerState]);

  const handleSubtitleSelect = useCallback(
    (subtitleId: string) => {
      setVideoPlayerState((prev) => ({
        ...prev,
        selectedSubtitle: subtitleId,
      }));
      // In a real app, you would apply the subtitle track here
    },
    [setVideoPlayerState],
  );

  const handleAudioSelect = useCallback(
    (audioId: string) => {
      setVideoPlayerState((prev) => ({
        ...prev,
        selectedAudio: audioId,
      }));
      // In a real app, you would apply the audio track here
    },
    [setVideoPlayerState],
  );

  const handleBackdropClick = useCallback(() => {
    // Clicking backdrop does nothing - user must use close button
  }, []);

  const cn = useClassNames(
    styles,
    styles["subtitle-overlay-wrapper"],
    className,
  );

  return (
    <AnimatePresence>
      {isSubtitleOverlayOpen && (
        <motion.div
          className={cn}
          onClick={handleBackdropClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <IconButton
            icon={StreamingIcons.Close}
            onClick={handleClose}
            ariaLabel="Close subtitle settings"
            className={styles["close-button"]}
          />
          <motion.div
            className={styles["subtitle-overlay"]}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            <VerticalScrollList
              heading="Subtitles"
              className={styles["vertical-scroll-list"]}
              headingClassName={styles["heading"]}
              scrollContainerClassName={styles["scroll-container"]}
            >
              {SUBTITLE_OPTIONS.map((option) => (
                <SingleSelect
                  key={option.id}
                  active={selectedSubtitle === option.id}
                  onClick={() => handleSubtitleSelect(option.id)}
                  className={styles["option"]}
                  textClassName={styles["option-text"]}
                >
                  {option.label}
                </SingleSelect>
              ))}
            </VerticalScrollList>
            <VerticalScrollList
              heading="Audio"
              className={styles["vertical-scroll-list"]}
              headingClassName={styles["heading"]}
              scrollContainerClassName={styles["scroll-container"]}
            >
              {AUDIO_OPTIONS.map((option) => (
                <SingleSelect
                  key={option.id}
                  active={selectedAudio === option.id}
                  onClick={() => handleAudioSelect(option.id)}
                  icon={option.icon}
                  className={styles["option"]}
                  textClassName={styles["option-text"]}
                >
                  {option.label}
                </SingleSelect>
              ))}
            </VerticalScrollList>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { SubtitleOverlay };
